import { Toast } from '../models';
import { createNotification } from './custom';

type LoggerType = 'log' | 'info' | 'debug' | 'trace' | 'table' | 'warn' | 'error';
type LoggerMethods = {
  notify: (options: NotificationMiddlewareOptions) => void;
};
type LoggerExecute = (...message: any[]) => LoggerMethods;
export type LoggerFunctions = {
  [K in LoggerType]?: LoggerExecute;
};
type ToastOptions = {
  [K in LoggerType]: Toast.Type;
};

interface NotificationMiddlewareOptions extends Toast.Options {
  title?: string;
  info?: string;
  message?: string;
}

/**
 * Refactored notificationMiddleware function
 *
 * @param {string} type - Optional parameter for notification type
 * @param {string} title - Optional parameter for notification title
 * @param {string[]} message - Array parameter for notification message
 * @param {object} options - Optional parameter for additional options
 * @returns {void}
 */
const notificationMiddleware = (
  type?: LoggerType,
  title?: string,
  message: any[] = [],
  options: NotificationMiddlewareOptions = {}
): void => {
  // making this var strongly typed so we're reminded to update it
  // if we add a new loggerType in the future
  const logToToastTypeMapping: ToastOptions = {
    log: Toast.Type.DEFAULT,
    info: Toast.Type.INFO,
    debug: Toast.Type.INFO,
    trace: Toast.Type.INFO,
    table: Toast.Type.INFO,
    warn: Toast.Type.WARNING,
    error: Toast.Type.DANGER,
  };
  const toastType: Toast.Type = logToToastTypeMapping[type || 'log'];
  const { message: optionalMessage, info, title: optionalTitle, ...otherOptions } = options;

  createNotification(optionalMessage || message.join('\n'), toastType, optionalTitle || title, info, otherOptions);
};

/**
 * Define a loggerMiddleware function that takes in a type and an optional title as parameters
 * @param type - The type of the logger
 * @param title - The title of the logger (optional)
 * @returns A function that takes in a variable number of messages and returns an object with a notify method
 */
const loggerMiddleware = (type: LoggerType, title?: string): LoggerExecute => {
  return (...message: string[]): LoggerMethods => {
    console[type](title || type.toUpperCase(), '|', ...message);
    return {
      notify: (options: NotificationMiddlewareOptions): void => {
        const notifyTitle = options?.title || title || type.toUpperCase();
        return notificationMiddleware(type, notifyTitle, message, options);
      },
    };
  };
};

const loggerTypes: LoggerType[] = ['info', 'debug', 'warn', 'error', 'trace', 'table'];
/**
 * Creates logger functions for logging different types of messages.
 *
 * @param title - The title of the logger.
 * @returns An object containing logger functions for different types of messages.
 */
const createLoggers = (title?: string): LoggerFunctions => {
  const result: LoggerFunctions = {
    log: loggerMiddleware('log', title),
  };
  for (const type of loggerTypes) {
    result[type] = loggerMiddleware(type, title);
  }
  return result;
};

const Logger = Object.freeze({
  ...createLoggers(),
  of: (title: string): LoggerFunctions => createLoggers(title),
});

export default Logger;

const debugLogger = (key: string, type: string = 'log'): ((...args: any[]) => void) => {
  const params = new URLSearchParams(window.location.search);
  const debug = params.get('debug');
  const debugKeys = (debug?.split?.(',') || []).filter(Boolean);
  const logKeys = (key?.split?.(',') || []).filter(Boolean);
  const validKeys = logKeys.filter((k: string): boolean => debugKeys.length === 0 || debugKeys.includes(k));
  const isDebug = debug !== null && (debugKeys.length === 0 || validKeys.length > 0);
  if (!isDebug) return (): void => {};
  return (...args: any[]): void => {
    const logged = new Set();
    for (let k = 0; k < logKeys.length; k++) {
      const log = logKeys[k];
      if (!validKeys.includes(log) || logged.has(key)) continue;
      logged.add(key);
      (console?.[type] || console.log)('[%cDEBUG%c]', 'color: skyblue;', '', log ?? 'GLOBAL', '|', ...args);
    }
  };
};
export const debug = {
  ...Object.keys(console).reduce(
    (acc, type) => {
      if (typeof console[type] === 'function')
        acc[type] = (key: string, ...args: any[]): void => {
          if (args.length === 0) {
            args[0] = key;
            key = undefined;
          }
          return debugLogger(key, type)(...args);
        };
      return acc;
    },
    {} as Record<string, (...args: any[]) => void>
  ),
  logger: (key: string) =>
    Object.keys(console).reduce(
      (acc, type) => {
        if (typeof console[type] === 'function')
          acc[type] = (...args: any[]): void => {
            if (args.length === 0) {
              args[0] = key;
              key = undefined;
            }
            return debugLogger(key, type)(...args);
          };
        return acc;
      },
      {} as Record<string, (...args: any[]) => void>
    ),
};
window['debug'] = debug;

declare global {
  const debug: {
    [K in keyof Console]: (key: string, ...args: any[]) => void;
  } & {
    logger: (key: string) => Console;
  };
}
